import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Button, CircularProgress, MenuItem, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNotifications } from "../../hooks/useNotifications";
import { openDialog } from "../../redux/reducer/dialogSlice";
import { showSnackbar } from "../../redux/reducer/snackbarSlice";
import { NotificationDetailsService, PendingActionNotificationService } from "../../utils/services/notification.service";
import { RootState } from "../../redux/store";
import { resetNotificationRefresh } from "../../redux/reducer/notificationRefreshSlice";


const NotificationList: React.FC<{ onViewMore: () => void }> = ({ onViewMore }) => {
    const pageSize = 10; 
    const page = 1;
    const [tab, setTab] = useState(1);
    const serviceFunction = tab === 0 ? NotificationDetailsService : PendingActionNotificationService;
    const dispatch = useDispatch();
   const { refresh: leavePolicyRefresh } = useSelector((state: RootState) => state.leavePolicy);
   const { refresh: attendanceRefresh } = useSelector((state: RootState) => state.attendance);


  const refreshNotifications = useSelector((state: RootState) => state.notificationRefetch.refreshNotifications);

    
    const { notifications, setNotifications, loading, error, refetch } =
      useNotifications(serviceFunction, page - 1, pageSize);
    
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setTab(newValue);
    };
  
    const formatDate = (date: dayjs.ConfigType): string => {
      return dayjs(date).format("DD MMM YYYY"); 
    };
    const cleanRegularizationType = (type: string): string => {
      if (!type) return '';
      const words = type.split('_').filter(Boolean); // remove empty strings
      if (words[words.length - 1].toLowerCase() === 'regularization') {
        words.pop(); // remove last word if it's "regularization"
      }
      return words.join(' ');
    };
    const generateNotificationMessage = (notification: any): string => {
      const { event, event_data, in_app_message } = notification; 
      const formatDateIfNeeded = (date: any): string => {
        if (!date) return '';
        if (typeof date === 'number' || /^\d+$/.test(date)) {
          return formatDate(new Date(Number(date)));
        }
        return date;
      };
      switch (event) {
        // AttendanceService events
        case "auto_clockout":
          return event_data.is_self
            ? `You have been automatically clocked out on ${formatDateIfNeeded(
                event_data.created_at
              )} due to inactivity. Please submit a regularization request if this was an error or if you need to adjust your attendance.`
            : `${
                event_data.to_user_name
              } was automatically clocked out on ${formatDateIfNeeded(
                event_data.created_at
              )} due to inactivity. Please review the situation if this affects any of your tasks.`;
    
        case "regularization_request":
          return event_data.is_self
            ? `You have submitted a ${
              cleanRegularizationType(event_data.regularization_type)
              } regularization request for ${formatDateIfNeeded(
                event_data.regularization_date
              )}. Please monitor your request status for updates.`
            : `${event_data.from_user_name} has submitted a ${
              cleanRegularizationType(event_data.regularization_type)
              } regularization request for ${formatDateIfNeeded(
                event_data.regularization_date
              )}.`;
    
        case "regularization_approve":
          return event_data.is_self
            ? `Your ${
              cleanRegularizationType(event_data.regularization_type)
              } regularization request for ${formatDateIfNeeded(
                event_data.regularization_date
              )} has been approved. Your attendance has been updated accordingly.`
            : `${event_data.from_user_name}'s ${
              cleanRegularizationType(event_data.regularization_type)
              } regularization request for ${formatDateIfNeeded(
                event_data.regularization_date
              )} has been approved.`;
    
        case "regularization_reject":
          return event_data.is_self
            ? `Your ${
              cleanRegularizationType(event_data.regularization_type)
              } regularization request for ${formatDateIfNeeded(
                event_data.regularization_date
              )} has been rejected. Please review the reason or contact your manager for clarification.`
            : `${event_data.from_user_name}'s ${
              cleanRegularizationType(event_data.regularization_type)
              } regularization request for ${formatDateIfNeeded(
                event_data.regularization_date
              )} has been rejected.`;
    
        case "regularization_cancel":
          return event_data.is_self
            ? `Your ${
              cleanRegularizationType(event_data.regularization_type)
              } regularization request for ${formatDateIfNeeded(
                event_data.regularization_date
              )} has been cancelled. If you need further assistance, please reach out to HR.`
            : `${event_data.from_user_name} has cancelled their ${
              cleanRegularizationType(event_data.regularization_type)
              } regularization request for ${formatDateIfNeeded(
                event_data.regularization_date
              )}.`;
    
        case "attendance_not_marked":
          return event_data.is_self
            ? `Your attendance for ${formatDateIfNeeded(
                event_data.attendance_date
              )} has not been marked. Kindly submit a regularization request as soon as possible to avoid discrepancies.`
            : `${event_data.to_user_name}'s attendance for ${formatDateIfNeeded(
                event_data.attendance_date
              )} was not marked. Please ensure this is addressed.`;
    
        case "leave_request":
          return event_data.is_self
            ? `You have submitted a leave request from ${formatDateIfNeeded(
                event_data.leave_start_date
              )} to ${formatDateIfNeeded(
                event_data.leave_end_date
              )}. Please wait for manager approval.`
            : `${event_data.from_user_name} has requested leave from ${formatDateIfNeeded(
                event_data.leave_start_date
              )} to ${formatDateIfNeeded(
                event_data.leave_end_date
              )}. Please review and take action if required.`;
    
        case "leave_approve":
          return event_data.is_self
            ? `Your leave from ${formatDateIfNeeded(
                event_data.leave_start_date
              )} to ${formatDateIfNeeded(
                event_data.leave_end_date
              )} has been approved. Enjoy your time off!`
            : `${event_data.from_user_name}'s ${
                event_data.leave_type
              } leave request from ${formatDateIfNeeded(
                event_data.leave_start_date
              )} to ${formatDateIfNeeded(
                event_data.leave_end_date
              )} has been approved.`;
    
        case "leave_reject":
          return event_data.is_self
            ? `Your leave request from ${formatDateIfNeeded(
                event_data.leave_start_date
              )} to ${formatDateIfNeeded(
                event_data.leave_end_date
              )} has been rejected. Please check the feedback or contact HR.`
            : `${event_data.from_user_name}'s ${
                event_data.leave_type
              } leave request from ${formatDateIfNeeded(
                event_data.leave_start_date
              )} to ${formatDateIfNeeded(
                event_data.leave_end_date
              )} has been rejected.`;
    
        case "leave_cancel":
          return event_data.is_self
            ? `You have cancelled your leave request for ${formatDateIfNeeded(
                event_data.leave_start_date
              )} to ${formatDateIfNeeded(
                event_data.leave_end_date
              )}. You may resubmit if needed.`
            : `${event_data.from_user_name} has cancelled their ${
                event_data.leave_type
              } leave request from ${formatDateIfNeeded(
                event_data.leave_start_date
              )} to ${formatDateIfNeeded(event_data.leave_end_date)}.`;
    
        // RecruitmentService events
        case "job_closed":
          return event_data.is_self
            ? `You have closed the job requisition for the position of ${event_data.position_name} (${event_data.job_code}). It is no longer accepting applications.`
            : `${event_data.created_by} has closed the job requisition for ${event_data.position_name} (${event_data.job_code}). Ensure all follow-up actions are completed.`;
            case "job_creation":
              return event_data.is_self
                ? `You have created a job requisition (${event_data.job_code}) for the position of ${event_data.position_name}. Please proceed with the approval process.`
                : `${event_data.created_by} has created a new job requisition (${event_data.job_code}) for the position of ${event_data.position_name}. Awaiting your approval.`;
            
    
                case "job_approved":
                  return `The job requisition (${event_data.job_code}) for "${event_data.position_name}" has been approved. You may proceed with candidate sourcing.`;
                
                case "job_rejected":
                  return `The job requisition (${event_data.job_code}) for "${event_data.position_name}" has been rejected. Please review the feedback and revise if necessary.`;
                
                case "job_published":
                  return event_data.is_self
                    ? `You have published the job posting for "${event_data.position_name}" (${event_data.job_code}). Candidates can now apply.`
                    : `${event_data.from_user_name} has published a job posting for "${event_data.position_name}" (${event_data.job_code}). It is now live for applications.`;
                
    
        case "candidate_creation":
          return `A new candidate named ${event_data.candidate_name} has been added to the recruitment system. Please review their application.`;
    
        case "candidate_screening":
          return `Screening process has started for candidate ${event_data.name}. Please proceed with the next steps.`;
    
        case "candidate_cv_shortlisted":
          return `Candidate ${event_data.name}'s CV has been shortlisted. Please schedule an interview or further review.`;
    
        case "candidate_cv_rejected":
          return `Candidate ${event_data.name}'s CV has been rejected. Please review the reasons for rejection if necessary.`;
    
        case "candidate_interview_scheduled":
          return `An interview has been scheduled for candidate ${
            event_data.name
          } on ${formatDateIfNeeded(
            event_data.interview_date
          )}. Please make arrangements accordingly.`;
    
        case "candidate_interview_completed":
          return `The interview for candidate ${event_data.name} has been completed. Please proceed with the next steps in the hiring process.`;
        case "candidate_interview_scheduled_interviewer":
          return event_data.is_self
            ? `You have been scheduled to interview ${
                event_data.candidate_name
              } for the position of ${event_data.job_title} on ${formatDateIfNeeded(
                event_data.interview_date
              )}. Please check your calendar and prepare accordingly.`
            : `${event_data.to_user_name} has been scheduled to interview ${
                event_data.candidate_name
              } for the position of ${event_data.job_title} on ${formatDateIfNeeded(
                event_data.interview_date
              )}.`;
    
        case "candidate_rejected":
          return event_data.is_self
            ? `You have been rejected for the position. We appreciate your application and encourage you to apply for future opportunities.`
            : `${event_data.name} has been rejected for the position.`;
    
        case "candidate_offered":
          return event_data.is_self
            ? `Congratulations! You have been offered the position of ${event_data.position_name}. Please review the offer and confirm your acceptance.`
            : `${event_data.name} has been offered the role of ${event_data.position_name}.`;
    
        case "candidate_hired":
          return `Candidate ${event_data.candidate_name} has been hired for the position of ${event_data.position_name}. Please welcome them to the team!`;
        case "candidate_cv_shortlisted_action":
          return event_data.is_self
            ? `Your profile has been shortlisted for the position of ${event_data.position_name}. Our recruitment team will contact you for the next steps.`
            : `${event_data.name}'s profile has been shortlisted for the position of ${event_data.position_name}. Please proceed with the next stage of the recruitment process.`;
        case "candidate_cv_rejected_action":
          return event_data.is_self
            ? `Your application for the position of ${event_data.position_name} has been rejected after CV screening by ${event_data.cv_screened_by}. We appreciate your interest and encourage you to apply for other suitable roles.`
            : `${event_data.name}'s CV for the position of ${event_data.position_name} has been rejected by ${event_data.cv_screened_by}. You can view the details here: ${event_data.url}`;
    
        // EmployeeService events
        case "confirmation":
          return event_data.is_self
            ? `Your confirmation process has been initiated. Please complete the necessary steps to finalize your confirmation.`
            : `Confirmation process has been initiated for ${event_data.employee_name}. Please review the next steps.`;
    
        case "confirmation_form_completed_by_employee":
          return event_data.is_self
            ? `You have successfully completed your confirmation form. Please wait for approval from your manager.`
            : `${event_data.from_user_name} has completed their confirmation form. Please review and approve as necessary.`;
    
        case "confirmation_form_approved_by_approver":
          return event_data.is_self
            ? `Your confirmation form has been approved by the approver. Congratulations on moving forward!`
            : `${event_data.from_user_name}'s confirmation form has been approved by the approver.`;
        case "confirmation_form_extended_by_approver":
          return event_data.is_self
            ? `Your confirmation period has been extended by your approver. Please review the updated details in your profile.`
            : `${event_data.from_user_name}'s confirmation period has been extended. Please ensure the employee is informed.`;
    
        // PayrollService events
        case "claim_request":
          return event_data.is_self
            ? `You have submitted a claim request for the bill dated ${formatDateIfNeeded(
                event_data.bill_date
              )}. You can track its status in the Claims section.`
            : `${
                event_data.from_user_name
              } has submitted a claim request for the bill dated ${formatDateIfNeeded(
                event_data.bill_date
              )}. Please review and take appropriate action.`;
    
        case "claim_approve":
          return event_data.is_self
            ? `Your claim request for the bill dated ${formatDateIfNeeded(
                event_data.bill_date
              )} has been approved. Reimbursement will be processed shortly.`
            : `You have approved ${
                event_data.from_user_name
              }'s claim request for the bill dated ${formatDateIfNeeded(
                event_data.bill_date
              )}.`;
    
        case "claim_reject":
          return event_data.is_self
            ? `Your claim request for the bill dated ${formatDateIfNeeded(
                event_data.bill_date
              )} has been rejected. Please check the reason and resubmit if needed.`
            : `${
                event_data.from_user_name
              } has rejected your claim request for the bill dated ${formatDateIfNeeded(
                event_data.bill_date
              )}.`;
    
        case "claim_cancel":
          return event_data.is_self
            ? `You have withdrawn your claim request for the bill dated ${formatDateIfNeeded(
                event_data.bill_date
              )}. Submit a new request if necessary.`
            : `${
                event_data.from_user_name
              } has cancelled the claim request for the bill dated ${formatDateIfNeeded(
                event_data.bill_date
              )}.`;
    
        // Other general events
        case "project_update":
          return `The project "${event_data.project_name}" has been updated. Please review the latest changes.`;
    
        case "compliance_deadline":
          return `The compliance deadline for "${event_data.compliance_name}" is approaching. Please ensure that all necessary actions are completed before the deadline.`;
    
        case "system_event":
          return `System event: "${event_data.event_description}". Please take note of any required actions.`;
    
        case "new_policy":
          return `A new policy titled "${event_data.policy_name}" has been published. Please review the policy and ensure compliance.`;
    
        case "ticket_assigned":
          return event_data.is_self
            ? `A new support ticket (No. ${event_data.ticket_no}) has been assigned to you. Please address it as soon as possible.`
            : `Support ticket (No. ${event_data.ticket_no}) has been assigned to ${event_data.to_user_name}. Please provide assistance.`;
    
        case "exit_request":
          return event_data.is_self
            ? `You have submitted an exit request. HR and your reporting manager will review and initiate the exit process.`
            : `${event_data.from_user_name} has submitted an exit request. Please begin the clearance and exit formalities.`;
        case "exit_request_withdrawn":
          return event_data.is_self
            ? `You have withdrawn your exit request. If this was unintentional or your plans change again, you can submit a new exit request.`
            : `${event_data.from_user_name} has withdrawn their exit request. No further exit processing is required unless a new request is submitted.`;
    
        case "ticket_reassigned":
          return event_data.is_self
            ? `A support ticket (${event_data.ticket_no}) assigned to you has been reassigned. Please check the ticket history for updates.`
            : `Ticket (${event_data.ticket_no}) has been reassigned to ${event_data.to_user_name}. Please review the updated assignment.`;
        case "ticket_status_update":
          return event_data.is_self
            ? `The status of your support ticket (${event_data.ticket_no}) has been updated to '${event_data.status}'. Please review the latest updates.`
            : `The status of ticket (${event_data.ticket_no}) assigned to ${event_data.to_user_name} has been updated to '${event_data.status}'.`;
        case "ticket_action":
          return event_data.is_self
            ? `An action has been taken on your support ticket (${event_data.ticket_no}): ${event_data.action_taken}. Please review the ticket for details.`
            : `An action has been taken on ticket (${event_data.ticket_no}) assigned to ${event_data.to_user_name}: ${event_data.action_taken}.`;
        case "employee_detail_update_by_employee":
          return event_data.is_self
            ? `You have updated your personal details. Please ensure all the information is accurate. If any corrections are needed, contact HR.`
            : `${event_data.from_user_name} has updated their personal details. Please review the changes and verify if any action is required.`;
    
        case "exit_request_rejected":
          return event_data.is_self
            ? `Your exit request has been rejected. Please check the comments from HR or your reporting manager for further clarification.`
            : `${event_data.from_user_name}'s exit request has been rejected. Please ensure the reason is communicated to the employee.`;
        case "relieving_letter_generation":
          return event_data.is_self
            ? `Your relieving letter has been generated. You can download it from the Exit section of your profile.`
            : `Relieving letter has been generated for ${event_data.from_user_name}. Please ensure it is reviewed and shared appropriately.`;
        case "exit_clearance_reject":
          return event_data.is_self
            ? `Your exit clearance request has been rejected. Please review the feedback and complete any pending items.`
            : `${event_data.from_user_name}'s exit clearance has been rejected. Ensure the necessary steps are communicated for completion.`;
        case "relieving_letter_send_on_mail":
          return event_data.is_self
            ? `Your relieving letter has been sent to your registered email address. Please check your inbox (and spam folder if necessary).`
            : `Relieving letter has been emailed to ${event_data.from_user_name}. Please confirm receipt if needed.`;
        case "candidate_rejected_interviewer":
          return event_data.is_self
            ? `You have marked ${event_data.candidate_name} as rejected for the position of ${event_data.job_title}. Your feedback has been recorded.`
            : `${event_data.from_user_name} has rejected ${event_data.candidate_name} for the position of ${event_data.job_title}. Please review if any further action is needed.`;
        case "onboard_request_created":
          return event_data.is_self
            ? `You have initiated an onboarding request for ${event_data.candidate_name}. Please ensure all necessary documents and approvals are processed.`
            : `${event_data.to_user_name} has created an onboarding request for ${event_data.candidate_name}. Review and proceed with the onboarding steps.`;
        case "onboard_request_approved":
          return event_data.is_self
            ? `Your onboarding request for ${event_data.candidate_name} has been approved. Proceed with completing the joining formalities.`
            : `${event_data.candidate_name}'s onboarding request for ${event_data.candidate_name} has been approved.`;
        case "candidate_document_reject":
          return event_data.is_self
            ? `One or more of your submitted documents have been rejected. Please review the feedback and re-upload the correct files.`
            : `${event_data.candidate_name}'s document(s) have been rejected. Ensure the candidate is notified to resubmit.`;
        case "candidate_document_approved":
          return event_data.is_self
            ? `Your submitted documents have been approved. You may proceed with the next steps of onboarding.`
            : `Documents submitted by ${event_data.candidate_name} have been approved.`;
        case "document_uploaded_by_candidate":
          return event_data.is_self
            ? `You have uploaded your documents successfully. They are now pending verification.`
            : `${event_data.candidate_name} has uploaded onboarding documents. Please review and verify them.`;
        case "document_link_shared_to_candidate":
          return event_data.is_self
            ? `A link to upload your documents has been sent to your registered email. Please complete the submission at the earliest.`
            : `A document upload link has been sent to ${event_data.candidate_name}. Monitor the status for timely submission.`;
        case "candidate_accepted_offer":
          return event_data.is_self
            ? `You have accepted the offer for the position of ${event_data.job_title}. Welcome aboard!`
            : `${event_data.candidate_name} has accepted the offer for the position of ${event_data.job_title}. Initiate onboarding process.`;
        case "candidate_rejected_offer":
          return event_data.is_self
            ? `You have declined the offer for the position of ${event_data.job_title}. We appreciate your interest and wish you the best.`
            : `${event_data.candidate_name} has declined the offer for the position of ${event_data.job_title}. Consider reaching out for feedback or next steps.`;
            case "candidate_offer_approval_request":
              return event_data.is_self
                ? `You have submitted an offer approval request for ${event_data.candidate_name} – ${event_data.designation} in the ${event_data.department_name} department. Please track its approval status.`
                : `${event_data.candidate_name} has submitted an offer approval request for ${event_data.candidate_name} – ${event_data.designation} in the ${event_data.department_name} department. Your review and approval are required.`;        
        case "candidate_offer_approval_reject":
          return event_data.is_self
            ? `Your offer approval request for ${event_data.candidate_name} has been rejected. Please review the remarks and revise if needed.`
            : `Offer approval request for ${event_data.candidate_name} by ${event_data.from_user_name} has been rejected. Please follow up accordingly.`;
            case "tax_declaration_started":
              return `
            📣 Income Tax Declaration Window Open
            
            Dear Employee,
            
            The window to declare your income tax investments for this financial year is now open. Kindly ensure you declare your eligible investments during this period.
            
            Declaration Start Date: ${formatDateIfNeeded(event_data.start_date)}
            Declaration End Date: ${formatDateIfNeeded(event_data.end_date)}
            
            Please complete your declarations before the deadline. For any questions or support, kindly contact the HR team.
            
            ---
            
            This is an automated message. Please do not reply to this notification.
              `;
            
        default:
          return in_app_message;
      }
    };
const handleRegularizeAttendance = (notification: any) => {
  dispatch(
    showSnackbar({
      message: "Feature under development 🚧",
      type: "info",
      open: true,
    })
  );
};

  const handleApplyLeave = (notification: any) => {
    dispatch(
      showSnackbar({
        message: "Feature under development 🚧",
        type: "info",
        open: true,
      })
    );
  };

  // const handleApprove = (notification: any,status: any) => {
  //   //notificationConfirmation
  //   dispatch(
  //     openDialog({
  //       type: "notificationConfirmation",
  //       title: "Confirmation",
  //       payload: { row: notification, status: status }
  //     })
  //   );
  //   // dispatch(
  //   //   showSnackbar({
  //   //     message: "Feature under development 🚧",
  //   //     type: "info",
  //   //     open: true,
  //   //   })
  //   // );
  // };

  const handleLeaveApprove = (id: any,uuid: any) => {   
      dispatch(openDialog({ type: 'LeaveRequestActionOnApproval', title: 'Approve Leave', status: "approve", id: id,notification_id: uuid }));
  };


  const handleLeaveReject = (id: any, uuid: any) => {
      dispatch(openDialog({ type: 'LeaveRequestActionOnApproval', title: 'Reject Leave', status: "reject", id: id, notification_id: uuid }));
  };

  const handleRegularizationApprove = (id: number,total_hours:string, uuid: any) => {    
    dispatch(openDialog({ type: 'AttendanceRegularizationStatusDialog', title: 'Update Regularization Status', status: "approve", id: id, notification_id: uuid, payload:{total_hours:total_hours} }));
  };


  const handleRegularizationReject = (id: number,total_hours:string, uuid: any) => {
    dispatch(openDialog({ type: 'AttendanceRegularizationStatusDialog', title: 'Update Regularization Status', status: "reject", id: id ,notification_id: uuid, payload:{total_hours:total_hours}}));
  };

  function calculateTimeDifference(inTime: any, outTime: any) {
     const [inHours, inMinutes] = inTime.split(':').map(Number);
     const [outHours, outMinutes] = outTime.split(':').map(Number);
 
     const inTotalMinutes = inHours * 60 + inMinutes;
     const outTotalMinutes = outHours * 60 + outMinutes;
 
     const differenceInMinutes = outTotalMinutes - inTotalMinutes;
 
     const hours = Math.floor(differenceInMinutes / 60);
     const minutes = differenceInMinutes % 60;
 
     const formattedHours = String(hours).padStart(2, '0');
     const formattedMinutes = String(minutes).padStart(2, '0');
 
    return `${formattedHours}:${formattedMinutes}`;
  }
  
 useEffect(() => {
  setTimeout(()=>{
    refetch(); 
   },500);
}, [leavePolicyRefresh,attendanceRefresh]);
const handleReject = (notification: any) => {
  dispatch(
    showSnackbar({
      message: "Feature under development 🚧",
      type: "info",
      open: true,
    })
  );
};

  const renderActions = (notification: any) => {
    switch (notification.event) {
      case "half_day_leave_request":
              return (
                 <Box display="flex" gap={1}>
                    <Button variant="outlined" color="success" onClick={() => handleLeaveApprove(notification?.correlation_id,notification?.uuid)}>Approve</Button>
                    <Button variant="outlined" color="error" onClick={() => handleLeaveReject(notification?.correlation_id,notification?.uuid)}>Reject</Button>
                  </Box>
              );
      case "regularization_request":
        return (
          <Box display="flex" gap={1}>
            <Button variant="outlined" color="success" onClick={() => handleRegularizationApprove(notification?.correlation_id,calculateTimeDifference(notification.event_data.clock_in_time,notification.event_data.clock_out_time),notification?.uuid)}>Approve</Button>
            <Button variant="outlined" color="error" onClick={() => handleRegularizationReject(notification?.correlation_id,calculateTimeDifference(notification.event_data.clock_in_time,notification.event_data.clock_out_time),notification?.uuid)}>Reject</Button>
          </Box>
        );
        // case "claim_request":
        // return (
        //   <Box display="flex" gap={1}>
        //     <Button variant="outlined" color="success" onClick={() => handleApprove(notification,"approve")}>Approve</Button>
        //     <Button variant="outlined" color="error" onClick={() => handleApprove(notification,"reject")}>Reject</Button>
        //   </Box>
        // );
        case "leave_request":
          return (
            <Box display="flex" gap={1}>
              <Button variant="outlined" color="success" onClick={() => handleLeaveApprove(notification?.correlation_id, notification?.uuid)}>Approve</Button>
              <Button variant="outlined" color="error" onClick={() => handleLeaveReject(notification?.correlation_id, notification?.uuid)}>Reject</Button>
            </Box>
          );
        
      default:
        return <Typography>-</Typography>;
    }
  };

  const handleMarkAsRead = (id: any) => {};

  return (
    <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      // height: "90vh",
      position: "relative", 
    }}
  >
    <Box
     sx={{
      position: "sticky",
      top: 0,
      zIndex: 10,
      bgcolor: "background.paper",
      borderBottom: 1,
      borderColor: "divider",
    }}
     >
    <Tabs
      value={tab}
      onChange={handleTabChange}
      variant="fullWidth"
      sx={{ borderBottom: 1, borderColor: "divider" }}
    >
      <Tab label="Notification" />
      <Tab label="Pending Action" />
    </Tabs>
    </Box>
    <Box
  sx={{
    flexGrow: 1,
    overflow: "auto",
  }}
>
  {/* Notifications Tab */}
  {tab === 0 ? (
    loading ? (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", mt: 15 }}>
        <CircularProgress color="primary" />
      </Box>
    ) : notifications?.notifications?.length > 0 ? (
      notifications.notifications.map((notification: any) => (
        <MenuItem
          key={notification.id}
          onClick={() => handleMarkAsRead(notification.id)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, mr: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              {notification.event
                .split("_")
                .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ")}
            </Typography>
            {/* <Tooltip title={generateNotificationMessage(notification)} arrow> */}
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                  minWidth: 0,
                }}
              >
                {notification?.in_app_message || generateNotificationMessage(notification)}
              </Typography>
            {/* </Tooltip> */}
            <Typography variant="caption" color="gray">
              {notification.timestamp}
            </Typography>
          </Box>
        </MenuItem>
      ))
    ) : (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          textAlign: "center",
          mt: 3,
          height: '90vh'
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mt: 5 }}>
          No notifications available
        </Typography>
      </Box>
    )
  ) : null}

  {/* Pending Action Tab */}
  {tab === 1 ? (
  loading ? (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", mt: 15 }}>
      <CircularProgress color="primary" />
    </Box>
  ) : (() => {
      const actionableStatusY = notifications?.actionable?.filter((n: any) => n.status === "Y") || [];
      return actionableStatusY.length > 0 ? (
        actionableStatusY.map((notification: any) => (
          <MenuItem
            key={notification.id}
            onClick={() => handleMarkAsRead(notification.id)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: "12px",
              gap: 1,
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Typography variant="body2" fontWeight="bold">
                {notification.event
                  .split("_")
                  .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")}
              </Typography>
              <Tooltip title={ notification?.in_app_message || generateNotificationMessage(notification)} arrow>
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    minWidth: 0,
                  }}
                >
                  {notification?.in_app_message || generateNotificationMessage(notification)}
                </Typography>
              </Tooltip>
              <Typography variant="caption" color="gray">
                {notification.timestamp}
              </Typography>
            </Box>
            <Box sx={{ width: "100%", mt: 1 }}>
              {renderActions(notification)}
            </Box>
          </MenuItem>
        ))
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            textAlign: "center",
            mt: 3,
            height: "90vh",
          }}
        >
          <Typography variant="h6" color="text.secondary" sx={{ mt: 5 }}>
            No pending actions available
          </Typography>
        </Box>
      );
    })()
) : null}


</Box>


    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        padding: 0,
        position: "sticky",
        bottom: 0,
        backgroundColor: "common.white",
        borderTop: "1px solid",
        borderColor: "divider",
        width: "100%",
      }}
    >
      <Button
        onClick={onViewMore}
        endIcon={<ChevronRightIcon />}
        sx={{ width: "100%", justifyContent: "center" }}
      >
        See all notifications
      </Button>
    </Box>
  </Box>
  );
};

export default NotificationList;



